import React, { SelectHTMLAttributes, useRef } from 'react';

import clsx from 'clsx';
import { createUseStyles } from 'react-jss';

import Icon from 'DesignSystem2/Icon/Icon';
import Text, { HeaderFontFeatureSettings } from 'DesignSystem2/Text';

import { Border, Palette, Radius, Spacing } from '../Theme';

const useStyles = createUseStyles({
    container: {
        padding: [Spacing(1.5), Spacing(2)],
        width: 'calc(100% + 2px)',
        display: 'block',
        boxSizing: 'border-box',
        textAlign: 'start',
        border: Border(Palette.Fill.x40),
        margin: '-1px',
        borderRadius: Radius.x30,
        transition: 'all 0.2s ease-in-out',
        outline: '0px solid transparent',
        cursor: 'pointer',
        position: 'relative',
    },
    containerEnabled: {
        composes: '$container',
        '&:hover': {
            borderRadius: Radius.x30,
            border: Border(Palette.Fill.x30),
        },
        '&:focus-within': {
            border: Border('#00D991', false),
            outline: '1px solid #00D991',
        },
    },
    containerDisabled: {
        composes: '$container',
        backgroundColor: Palette.Fill.x50,
        cursor: 'default',
    },
    row: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    errorSquare: {
        display: 'inline-block',
        borderRadius: Radius.x10,
        height: 8,
        width: 8,
        marginRight: Spacing(1),
        backgroundColor: Palette.Critical.x10,
    },
    selectContainer: {
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: Palette.Text.x10,
    },
    input: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyItems: 'end',
        paddingBottom: '12px',
        width: '100%',
        height: '100%',
        fontFamily: 'var(--inter-font)',
        fontFeatureSettings: HeaderFontFeatureSettings,
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 400,
        border: 0,
        outline: 0,
        padding: 0,
        paddingLeft: '16px',
        paddingRight: '30px',
        backgroundColor: 'transparent',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1,
        cursor: 'pointer',
        '&::placeholder': {
            color: Palette.Text.x30,
            fontFeatureSettings: HeaderFontFeatureSettings,
            fontFamily: 'var(--inter-font)',
        },
    },
    inputEnabled: {
        composes: '$input',
        color: Palette.Text.x10,
    },
    inputDisabled: {
        composes: '$input',
        color: Palette.Text.x20,
        cursor: 'default',
    },
    expandIcon: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        zIndex: 2,
    },
});

interface BaseSelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
    labelText?: string;
    row?: boolean;
    selectedText?: string;
}
interface ErrorSelectInputProps extends BaseSelectInputProps {
    errorText?: string;
    helperText?: never;
}
interface HelperSelectInputProps extends BaseSelectInputProps {
    errorText?: never;
    helperText?: string;
}
type SelectInputProps = ErrorSelectInputProps | HelperSelectInputProps;

const SelectInput = ({
    labelText = '',
    row = false,
    selectedText = '',
    errorText = '',
    helperText = '',
    className,
    ...selectProps
}: SelectInputProps): JSX.Element => {
    const classes = useStyles();
    const { disabled } = selectProps;
    const selectRef = useRef<HTMLSelectElement>(null);

    const handleContainerClick = (event: React.MouseEvent) => {
        // Don't trigger if the click came from the select element itself
        if (!disabled && selectRef.current && event.target !== selectRef.current) {
            selectRef.current.focus();

            // Try modern showPicker() method first
            if ('showPicker' in selectRef.current) {
                try {
                    (selectRef.current as any).showPicker();
                    return;
                } catch (error) {
                    // Fall through to other methods
                }
            }

            // Fallback: Create a synthetic mousedown event
            const event = new MouseEvent('mousedown', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            selectRef.current.dispatchEvent(event);
        }
    };

    const label = !!labelText && (
        <Text.Body.x20 color={disabled ? Palette.Text.x20 : Palette.Text.x10}>{labelText}</Text.Body.x20>
    );
    const helper = !!helperText && <Text.Body.x20 color={Palette.Text.x20}>{helperText}</Text.Body.x20>;
    const error = !!errorText && (
        <span>
            <span className={classes.errorSquare} />
            <Text.Body.x20 color={Palette.Critical.x10}>{errorText}</Text.Body.x20>
        </span>
    );

    const ExpandIcon = Icon['ExpandMore'];

    return (
        <label
            className={clsx(className, disabled ? classes.containerDisabled : classes.containerEnabled)}
            onClick={handleContainerClick}
        >
            <span className={clsx(row && classes.row)}>
                {!!labelText && <span className={'opacity-50'}>{label}</span>}
                <div className={classes.selectContainer}>
                    {selectedText || (
                        <select
                            ref={selectRef}
                            className={clsx(disabled ? classes.inputDisabled : classes.inputEnabled)}
                            disabled={disabled}
                            {...selectProps}
                        />
                    )}
                    <div className={classes.expandIcon}>
                        <ExpandIcon color={Palette.Fill.x20} size={'24px'} />
                    </div>
                </div>
            </span>

            {(!!helper || !!error) && (
                <span style={{ display: 'block', marginTop: Spacing(0.25) }}>
                    {helper} {error}
                </span>
            )}
        </label>
    );
};

SelectInput.displayName = 'SelectInput';
export default SelectInput;
